import 'react-phone-number-input/style.css';

import { ReactNode, useId } from 'react';
import PhoneInput, { type Country, type Value } from 'react-phone-number-input';

import { InputLabel } from '@/components/InputLabel';

import { cn } from '@/lib/utils';

type PhoneNumberInputProps = {
  value?: string;
  onChange?: (value: Value | undefined) => void;
  label?: ReactNode;
  hint?: ReactNode;
  isRequired?: boolean;
  isInvalid?: boolean;
  'aria-label'?: string;
  disabled?: boolean;
  defaultCountry?: Country;
  id?: string;
  className?: string;
};

const DEFAULT_COUNTRY: Country = 'UA';

const PhoneNumberInput = (props: PhoneNumberInputProps) => {
  const {
    value,
    onChange,
    label,
    hint,
    isRequired,
    isInvalid,
    'aria-label': ariaLabel,
    disabled,
    defaultCountry = DEFAULT_COUNTRY,
    id,
    className,
  } = props;

  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <InputLabel
          htmlFor={inputId}
          label={label}
          hint={hint}
          isRequired={isRequired}
          isInvalid={isInvalid}
        />
      )}
      <PhoneInput
        id={inputId}
        aria-label={ariaLabel}
        value={value as Value | undefined}
        onChange={(newValue) => onChange?.(newValue)}
        defaultCountry={defaultCountry}
        international
        countryCallingCodeEditable={false}
        disabled={disabled}
        className={cn(
          'phone-input',
          'border-input focus-within:border-primary bg-card hover:bg-accent flex h-8 w-full items-center rounded-md border px-3 text-sm transition-colors',
          isInvalid && 'border-destructive focus-within:border-destructive',
          'has-disabled:text-muted-foreground has-disabled:bg-muted has-disabled:hover:bg-muted has-disabled:border-border has-disabled:cursor-not-allowed',
          className,
        )}
      />
    </div>
  );
};

export { PhoneNumberInput };
export type { PhoneNumberInputProps };
